import * as THREE from 'three';

// FRAME-COST DIAGNOSTICS (dev tool, toggled with P). Answers "WHERE does the frame time go inland / near
// mountains?" without guessing, by measuring three things every frame it is active:
//   1. CPU (JS main-thread) time      -- start..end of the frame callback (streaming, collision, meshing, sim).
//   2. GPU time                       -- a WebGL2 timer query wrapped around the render (fill, geometry, shadows).
//                                        If CPU >> GPU you are CPU-bound; if GPU >> CPU you are GPU-bound.
//   3. renderer.info + per-system attribution -- draw calls, triangles, geometry/texture/program counts, and a
//                                        throttled scene-graph walk that splits VISIBLE triangles + instances by
//                                        subsystem (terrain / grass / trees / rocks / ...), so the biggest
//                                        contributor near a mountain is obvious.
// Purely observational: it reads counters and never changes render state. All work is skipped when inactive.

export type PerfRoot = { name: string; obj: THREE.Object3D };

// Visible triangles drawn for ONE object (InstancedMesh counts its drawn instances). Hidden -> 0, like the renderer.
function objTris(o: THREE.Object3D): number {
  const m = o as THREE.Mesh & { isMesh?: boolean; isInstancedMesh?: boolean; count?: number };
  if (!m.isMesh || !m.visible || !m.geometry) return 0;
  const g = m.geometry as THREE.BufferGeometry;
  const verts = g.index ? g.index.count : g.attributes.position ? g.attributes.position.count : 0;
  const inst = m.isInstancedMesh ? m.count ?? 0 : 1;
  return (verts / 3) * inst;
}

type Bucket = { drawn: number; loaded: number; meshes: number; instances: number };
const emptyBucket = (): Bucket => ({ drawn: 0, loaded: 0, meshes: 0, instances: 0 });

const _fSphere = new THREE.Sphere();

// Does the renderer actually DRAW this mesh (does it survive frustum culling)? Mirrors three.js: a frustumCulled=false
// mesh always draws; an InstancedMesh tests its own instance-spanning sphere (this is why trees -- one big instanced
// mesh -- are never per-instance culled); a normal mesh tests its geometry sphere in world space (per-chunk grass/terrain).
function meshDrawn(o: THREE.Mesh & { isInstancedMesh?: boolean; boundingSphere?: THREE.Sphere | null; computeBoundingSphere?: () => void }, fr: THREE.Frustum): boolean {
  if (o.frustumCulled === false) return true;
  if (o.isInstancedMesh) {
    if (!o.boundingSphere && o.computeBoundingSphere) o.computeBoundingSphere();
    return o.boundingSphere ? fr.intersectsSphere(o.boundingSphere) : true;
  }
  const g = o.geometry as THREE.BufferGeometry;
  if (!g.boundingSphere) g.computeBoundingSphere();
  if (!g.boundingSphere) return true;
  _fSphere.copy(g.boundingSphere).applyMatrix4(o.matrixWorld);
  return fr.intersectsSphere(_fSphere);
}

// Fold a subtree's renderables into `acc`, splitting LOADED (the .visible geometry streamed around the player) from
// DRAWN (also survives the camera frustum -- what the GPU keeps). The gap is what per-mesh frustum culling saves, so
// a system that streams a full 360 disc (grass/terrain) reads far smaller in `drawn` than in `loaded`.
function foldTree(root: THREE.Object3D, acc: Bucket, fr: THREE.Frustum): void {
  if (!root.visible) return;
  const m = root as THREE.Mesh & { isMesh?: boolean; isInstancedMesh?: boolean; count?: number };
  if (m.isMesh && m.geometry && (m.count === undefined || m.count > 0)) {
    const t = objTris(root);
    acc.loaded += t;
    acc.meshes += 1;
    if (m.isInstancedMesh) acc.instances += m.count ?? 0;
    if (meshDrawn(m, fr)) acc.drawn += t;
  }
  const kids = root.children;
  for (let i = 0; i < kids.length; i++) foldTree(kids[i], acc, fr);
}

export class PerfProbe {
  active = false;
  // Set TRUE by the frame loop on any frame the sun shadow map re-renders (moved ~1m / sun crept / zoom changed). The
  // shadow pass is a prime inland/mountain suspect -- more + taller casters (dense trees, rocks, mountains) make each
  // rebuild heavy, and they rebuild constantly while moving. Splitting GPU time by this flag isolates that cost.
  shadowRebuiltThisFrame = false;

  private cpuMs = 0;   // smoothed JS main-thread time per frame (ms)
  private gpuMs = 0;   // smoothed GPU render time (ms), all frames; 0 when the timer-query extension is unavailable
  private gpuSteadyMs = 0;  // smoothed GPU on frames WITHOUT a shadow rebuild
  private gpuRebuildMs = 0;  // smoothed GPU on frames WITH a shadow rebuild (steady + the shadow pass)
  private t0 = 0;      // frame-start timestamp (performance.now)
  private shadowCount = 0; private shadowRate = 0; private rateT = -1; // shadow rebuilds/sec

  // WebGL2 GPU timer query (EXT_disjoint_timer_query_webgl2). One query in flight; result lands a frame or two later.
  private readonly gl: WebGL2RenderingContext | null;
  private readonly ext: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  private query: WebGLQuery | null = null;
  private queryOpen = false;   // between beginQuery/endQuery this frame
  private queryInFlight = false; // ended, awaiting a result
  private queryWasRebuild = false; // did the in-flight query's frame rebuild the shadow map?

  // renderer.info snapshot
  private calls = 0; private tris = 0; private geometries = 0; private textures = 0; private programs = 0;
  // Per-system attribution (throttled), plus the "other" (unlabelled) remainder + scene totals.
  private readonly frustum = new THREE.Frustum();
  private readonly frMat = new THREE.Matrix4();
  private attrib: { name: string; b: Bucket }[] = [];
  private other: Bucket = emptyBucket();
  private sceneDrawn = 0; private sceneLoaded = 0;
  private lastAttrib = -1;
  // Tree occlusion cull readout (fed each frame by the loop): trees drawn / off-screen / actually hidden + on-state.
  private cullDrawn = 0; private cullOffscreen = 0; private cullHidden = 0; private cullOn = false;

  constructor(renderer: THREE.WebGLRenderer, private readonly scene: THREE.Scene, private readonly roots: PerfRoot[]) {
    // renderer.info auto-resets on EVERY renderer.render(), and the EffectComposer runs several passes per frame, so
    // by end-of-frame info reflects only the LAST (FXAA) pass -> "1 draw". Turn auto-reset off and reset once per
    // frame ourselves (in the loop) so info accumulates the TRUE per-frame total: shadow + scene + every post pass.
    renderer.info.autoReset = false;
    const gl = renderer.getContext();
    if (typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext) {
      this.gl = gl;
      const e = gl.getExtension('EXT_disjoint_timer_query_webgl2');
      this.ext = e as unknown as { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
    } else {
      this.gl = null; this.ext = null;
    }
  }

  // Tree occlusion-cull counts, pushed by the frame loop. drawn = streamed trees shown; offscreen = outside the view
  // (the waste); hidden = actually culled this frame (0 when the O toggle is off, so you see the potential first).
  setCull(drawn: number, offscreen: number, hidden: number, on: boolean): void {
    this.cullDrawn = drawn; this.cullOffscreen = offscreen; this.cullHidden = hidden; this.cullOn = on;
  }

  // Frame top: stamp the CPU start (only while active, so an inactive probe is truly free).
  begin(): void { if (this.active) this.t0 = performance.now(); }

  // Just before the render: open a GPU timer query around it (polls the previous frame's result first).
  beforeRender(): void {
    if (!this.active) { this.shadowRebuiltThisFrame = false; return; }
    if (this.shadowRebuiltThisFrame) this.shadowCount++; // tally for the rebuilds/sec readout
    if (!this.gl || !this.ext) { this.shadowRebuiltThisFrame = false; return; }
    this.pollGpu();
    if (!this.queryInFlight) {
      if (!this.query) this.query = this.gl.createQuery();
      if (this.query) { this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, this.query); this.queryOpen = true; this.queryWasRebuild = this.shadowRebuiltThisFrame; }
    }
    this.shadowRebuiltThisFrame = false; // consumed; the frame loop re-sets it next rebuild
  }

  // Just after the render: close the query so it starts resolving.
  afterRender(): void {
    if (!this.queryOpen || !this.gl || !this.ext) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.queryOpen = false; this.queryInFlight = true;
  }

  private pollGpu(): void {
    if (!this.gl || !this.ext || !this.query || !this.queryInFlight) return;
    const available = this.gl.getQueryParameter(this.query, this.gl.QUERY_RESULT_AVAILABLE) as boolean;
    if (!available) return;
    const disjoint = this.gl.getParameter(this.ext.GPU_DISJOINT_EXT) as boolean;
    if (!disjoint) {
      const ms = (this.gl.getQueryParameter(this.query, this.gl.QUERY_RESULT) as number) / 1e6;
      this.gpuMs += (ms - this.gpuMs) * 0.2; // smoothed, all frames
      // Split by whether that frame re-rendered the shadow map -> the gap = the shadow pass's own GPU cost.
      if (this.queryWasRebuild) this.gpuRebuildMs += (ms - this.gpuRebuildMs) * 0.3;
      else this.gpuSteadyMs += (ms - this.gpuSteadyMs) * 0.2;
    }
    this.queryInFlight = false;
  }

  // Frame end: fold in the CPU time, snapshot renderer.info, and (throttled) re-attribute the scene by subsystem.
  // Called AFTER the render, so camera.matrixWorldInverse is fresh -> the frustum split (drawn vs loaded) is exact.
  end(renderer: THREE.WebGLRenderer, camera: THREE.Camera): void {
    if (!this.active) return;
    this.cpuMs += (performance.now() - this.t0 - this.cpuMs) * 0.2; // smoothed
    const info = renderer.info;
    this.calls = info.render.calls;
    this.tris = info.render.triangles;
    this.geometries = info.memory.geometries;
    this.textures = info.memory.textures;
    this.programs = info.programs ? info.programs.length : 0;

    const t = performance.now();
    if (this.rateT < 0) this.rateT = t;
    if (t - this.rateT >= 1000) { this.shadowRate = (this.shadowCount * 1000) / (t - this.rateT); this.shadowCount = 0; this.rateT = t; }
    if (t - this.lastAttrib > 250) { // ~4 Hz: the scene walk is cheap but not worth every frame
      this.lastAttrib = t;
      this.frMat.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
      this.frustum.setFromProjectionMatrix(this.frMat);
      const all = emptyBucket();
      foldTree(this.scene, all, this.frustum);
      const labelled = emptyBucket();
      this.attrib = this.roots.map((r) => {
        const b = emptyBucket();
        foldTree(r.obj, b, this.frustum);
        labelled.drawn += b.drawn; labelled.loaded += b.loaded; labelled.meshes += b.meshes; labelled.instances += b.instances;
        return { name: r.name, b };
      });
      this.attrib.sort((a, b) => b.b.drawn - a.b.drawn);
      this.sceneDrawn = all.drawn; this.sceneLoaded = all.loaded;
      this.other = {
        drawn: Math.max(0, all.drawn - labelled.drawn),
        loaded: Math.max(0, all.loaded - labelled.loaded),
        meshes: Math.max(0, all.meshes - labelled.meshes),
        instances: Math.max(0, all.instances - labelled.instances),
      };
    }
  }

  private static k(n: number): string { return n >= 1e6 ? (n / 1e6).toFixed(2) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(0) + 'k' : String(Math.round(n)); }

  // Formatted panel lines (the overlay renders these verbatim).
  lines(): string[] {
    const bound = this.gpuMs <= 0 ? '—' : this.cpuMs > this.gpuMs * 1.15 ? 'CPU-bound' : this.gpuMs > this.cpuMs * 1.15 ? 'GPU-bound' : 'balanced';
    const out = [
      'OXIDE perf (P)',
      `frame    ${this.cpuMs.toFixed(2)} ms CPU   ${this.gpuMs > 0 ? this.gpuMs.toFixed(2) + ' ms GPU' : 'GPU n/a'}   ${bound}`,
      `render   ${this.calls} draws   ${PerfProbe.k(this.tris)} tris`,
      `memory   ${this.geometries} geom   ${this.textures} tex   ${this.programs} shaders`,
    ];
    // Shadow attribution: rebuilds/sec + the GPU gap between rebuild and steady frames == the shadow pass cost.
    if (this.gpuMs > 0) {
      const shadowCost = Math.max(0, this.gpuRebuildMs - this.gpuSteadyMs);
      out.push(`shadow   ${this.shadowRate.toFixed(0)}/s rebuilds   steady ${this.gpuSteadyMs.toFixed(2)}  onRebuild ${this.gpuRebuildMs.toFixed(2)}  (+${shadowCost.toFixed(2)} ms)`);
    } else {
      out.push(`shadow   ${this.shadowRate.toFixed(0)}/s rebuilds   (GPU timing n/a on this browser)`);
    }
    // Occlusion waste: how many streamed trees sit OUTSIDE the view (drawn for nothing) and what the O cull removes.
    const streamed = this.cullDrawn + this.cullHidden;
    const wastePct = streamed > 0 ? Math.round((this.cullOffscreen / streamed) * 100) : 0;
    out.push(`occlude  ${streamed} trees   ${this.cullOffscreen} off-screen (${wastePct}% waste)   ${this.cullOn ? `O:ON −${this.cullHidden}` : 'O:off — press O'}`);
    // Scene culling summary: DRAWN (survives the frustum) vs LOADED (streamed around you). A big gap = frustum
    // culling working; a system whose drawn ~= loaded (trees: one instanced mesh) is NOT being view-culled.
    const cullPct = this.sceneLoaded > 0 ? Math.round((1 - this.sceneDrawn / this.sceneLoaded) * 100) : 0;
    out.push(`scene    ${PerfProbe.k(this.sceneDrawn)} drawn / ${PerfProbe.k(this.sceneLoaded)} loaded  (${cullPct}% frustum-culled)`);
    out.push('by system (drawn / loaded tris):');
    for (const a of this.attrib) {
      if (a.b.loaded <= 0 && a.b.instances <= 0) continue;
      const tag = a.b.instances ? `${PerfProbe.k(a.b.instances)} inst` : `${a.b.meshes} mesh`;
      out.push(`  ${a.name.padEnd(11)} ${PerfProbe.k(a.b.drawn).padStart(7)} / ${PerfProbe.k(a.b.loaded).padStart(7)}   ${tag}`);
    }
    if (this.other.loaded > 0) out.push(`  ${'other'.padEnd(11)} ${PerfProbe.k(this.other.drawn).padStart(7)} / ${PerfProbe.k(this.other.loaded).padStart(7)}   ${this.other.meshes} mesh`);
    return out;
  }
}
